/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/04/2022
 */

import * as React from 'react';
import { Fragment } from 'react';
import Page from '@/wpt-lib/pdf/common/Page';
import { LabelCell, Row, Table, ValueCell } from '@/wpt-lib/pdf/common/Table';
import useData, { useLabels } from '@/wpt-lib/pdf/useData';
import Heading from '@/wpt-lib/pdf/common/Heading';
import { label } from '@/wpt-lib/utils/label';
import { mapBookedLeaveCodes } from '@/wpt-lib/interfaces/Leave';
import { parseFormData } from '@/wpt-lib/utils/formData';
import SectionComments from '@/wpt-lib/pdf/SectionComments';
import { WorkplanCommentSection } from '@/wpt-lib/interfaces/WorkplanComment';
import SubHeading from '@/wpt-lib/pdf/common/SubHeading';
import ProgressUpdates from '@/wpt-lib/pdf/ProgressUpdates';

const Leave = () => {
    const data = useData();

    const leaves = data.leaves.map((leave) => {
        const form = parseFormData(leave.workplanItem.formData);
        return {
            type: form.absenceType,
            code: form.leaveCode,
            statement: form.discussedLeave,
            startDate: leave.startDate,
            endDate: leave.endDate,
            days: leave.days,
            comment: form.comments,
            workplanItem: leave.workplanItem,
        };
    });

    const ascender = data.bookedLeaves;

    const absenceType = useLabels('absenceType');
    const leaveCode = useLabels('leaveCode');

    const comments = data.comments.filter((c) => c.section === WorkplanCommentSection.LEAVE);

    return (
        <Page>
            <Heading text="Leave" />
            <Table cols={4}>
                <Row>
                    <SubHeading level={2} text="Planned Absences" />
                </Row>
                {ascender.map((leave, i) => (
                    <Fragment key={i}>
                        <Row>
                            <ValueCell label="Ascender Booked Leave" value={label(mapBookedLeaveCodes, leave.code)} />
                            <ValueCell label="Start Date" value={leave.startDate} format="date" />
                            <ValueCell label="End Date" value={leave.endDate} format="date" />
                            <ValueCell label="Time Days" value={Math.round((leave.hours * 100) / 7) / 100} />
                        </Row>
                    </Fragment>
                ))}

                {ascender.length === 0 && (
                    <Row>
                        <LabelCell label="No booked leave." />
                    </Row>
                )}

                <Row>
                    <SubHeading level={2} text="Other Planned Absences" />
                </Row>
                {leaves.map((leave, i) => (
                    <Fragment key={i}>
                        <Row>
                            <ValueCell label="Leave Code" value={label(leaveCode, leave.code)} />
                            <ValueCell label="Absence Type" value={label(absenceType, leave.type)} />
                            <ValueCell label="Start Date" value={leave.startDate} format="date" />
                            <ValueCell label="End Date" value={leave.endDate} format="date" />
                            <ValueCell label="Time Days" value={leave.days} />
                        </Row>
                        <ProgressUpdates item={leave.workplanItem} header={false} spacer />
                        {data.workplan.year < 2024 && (
                            <Row>
                                <ValueCell
                                    label="Statement"
                                    value={
                                        leave.statement
                                            ? 'I have discussed my leave plans with my supervisor for this workplanning year.'
                                            : 'I have not discussed my leave plans with my supervisor for this workplanning year.'
                                    }
                                />
                            </Row>
                        )}

                        {leave.comment && (
                            <Row>
                                <ValueCell label="Comments" value={leave.comment} />
                            </Row>
                        )}
                    </Fragment>
                ))}

                {leaves.length === 0 && (
                    <Row>
                        <LabelCell label="No planned leave." />
                    </Row>
                )}
            </Table>

            <SectionComments heading="Leave Comments" comments={comments} />
        </Page>
    );
};

export default Leave;
